import { useEffect, useState } from "react";
import { prefersReducedMotion } from "./motion";

const DURATION = 1200;

/**
 * "1.2K", "1,2B", "12.345", "845" gibi gösterim metnini sayı + ek olarak çözer.
 * Ayraçtan sonra tam üç hane gruplar halinde geliyorsa binlik ayraç sayılır,
 * aksi halde ondalık ayraç. Çözülemezse null döner.
 */
export function parseDisplay(display) {
  const match = /^(\d+(?:[.,]\d+)*)(.*)$/.exec(String(display ?? "").trim());
  if (!match) return null;
  const [, numeric, suffix] = match;
  const sepMatch = /[.,]/.exec(numeric);

  if (!sepMatch) {
    return { target: Number(numeric), decimals: 0, sep: null, grouping: false, suffix };
  }

  const sep = sepMatch[0];
  const parts = numeric.split(sep);
  if (/[.,]/.test(parts.join(""))) return null; // iki farklı ayraç: çözme

  const grouping = parts.length > 1 && parts.slice(1).every((part) => part.length === 3) && !suffix.trim();
  if (grouping) {
    return { target: Number(parts.join("")), decimals: 0, sep, grouping: true, suffix };
  }
  if (parts.length !== 2) return null;
  return {
    target: Number(`${parts[0]}.${parts[1]}`),
    decimals: parts[1].length,
    sep,
    grouping: false,
    suffix,
  };
}

function format(n, { decimals, sep, grouping, suffix }) {
  if (grouping) {
    const digits = String(Math.round(n));
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + suffix;
  }
  const fixed = n.toFixed(decimals);
  return (sep ? fixed.replace(".", sep) : fixed) + suffix;
}

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Sayıyı 0'dan hedefe sayar; `start` true olunca başlar. Bitişte sunucudan
 * gelen metnin aynısı yazılır. Hareket azaltılmışsa doğrudan son değer görünür.
 */
export default function CountUp({ value, start = true, className = "" }) {
  const parsed = parseDisplay(value);
  const [shown, setShown] = useState(() =>
    !parsed || prefersReducedMotion() ? value : format(0, parsed)
  );

  useEffect(() => {
    const info = parseDisplay(value);
    if (!info || prefersReducedMotion()) {
      setShown(value);
      return undefined;
    }
    if (!start) {
      setShown(format(0, info));
      return undefined;
    }

    let frame;
    let startedAt;
    const tick = (now) => {
      if (startedAt === undefined) startedAt = now;
      const t = Math.min((now - startedAt) / DURATION, 1);
      if (t >= 1) {
        setShown(value);
        return;
      }
      setShown(format(info.target * easeOutCubic(t), info));
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [value, start]);

  return (
    <span className={`tabular-nums ${className}`} aria-label={value}>
      {shown}
    </span>
  );
}
